import { getCountryAbbreviation, getCountryRaw } from '@heidi/utils/get-country';

/**
 * Fetches the user's country from the server using Next.js / Vercel geolocation headers.
 * Returns the English country name (e.g. "Australia") or null if unavailable.
 *
 * Prefer this over getCountryRaw() which relies on the browser's timezone,
 * which is unreliable (users may have incorrect system clocks or use VPNs that
 * don't change their timezone).
 */

type GeoResult = {
  country: string | null;
  countryCode: string | null;
};

let cachedGeo: GeoResult | undefined;

async function fetchGeo(): Promise<GeoResult> {
  if (cachedGeo !== undefined) return cachedGeo;

  try {
    const res = await fetch('/api/geo');
    if (!res.ok) {
      cachedGeo = { country: null, countryCode: null };
      return cachedGeo;
    }
    const data = (await res.json()) as GeoResult;
    // x-vercel-ip-country is only set on Vercel's edge — fall back to
    // timezone-based detection on localhost / non-Vercel environments.
    if (data.countryCode === null) {
      cachedGeo = {
        country: getCountryRaw(),
        countryCode: getCountryAbbreviation(),
      };
      return cachedGeo;
    }
    cachedGeo = data;
    return cachedGeo;
  } catch {
    cachedGeo = { country: null, countryCode: null };
    return cachedGeo;
  }
}

export async function getCountryFromGeo(): Promise<string | null> {
  const geo = await fetchGeo();
  return geo.country;
}

/** ISO 3166-1 alpha-2 code from `x-vercel-ip-country`, or timezone fallback. */
export async function getCountryCodeFromGeo(): Promise<string | null> {
  const geo = await fetchGeo();
  return geo.countryCode;
}

export function resetCountryCache(): void {
  cachedGeo = undefined;
}
